import React from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useAcademicRenewalFormState } from './hooks/useAcademicRenewalFormState';
import AcademicRenewalFormFields from './AcademicRenewalFormFields';
import './AcademicRenewalForm.css';
import SharedButton from '@/global/Button/Button';
import type { RenewalFormData } from './renewalFormTypes';
import { APP_MODAL_NESTED_Z_INDEX } from '@/constants/modalZIndex';

export type { RenewalFormData };

interface AcademicRenewalFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: RenewalFormData) => Promise<void>;
  loading: boolean;
}

const AcademicRenewalForm: React.FC<AcademicRenewalFormProps> = ({ open, onClose, onSubmit, loading }) => {
  const { formData, errors, validateForm, handleInputChange, setMultiSelect, t } =
    useAcademicRenewalFormState();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting renewal form:', error);
    }
  };

  if (typeof document === 'undefined' || !open) {
    return null;
  }

  return createPortal(
    <Dialog open={open} onClose={onClose} style={{ position: 'relative', zIndex: APP_MODAL_NESTED_Z_INDEX }}>
      <DialogBackdrop
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        style={{ zIndex: APP_MODAL_NESTED_Z_INDEX }}
      />

      <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: APP_MODAL_NESTED_Z_INDEX }}>
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel className="relative transform overflow-hidden rounded-xl bg-white px-4 pb-6 pt-6 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-8">
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 inline-flex justify-center rounded-full p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>

            <div className="text-center mb-6">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-pink-100 to-purple-100 mb-4">
                <DocumentTextIcon className="w-8 h-8 text-pink-600" />
              </div>
              <DialogTitle as="h3" className="text-xl font-bold leading-6 text-gray-900">
                {t.title}
              </DialogTitle>
              <p className="mt-2 text-sm text-gray-600">{t.description}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <AcademicRenewalFormFields
                mode="full"
                formData={formData}
                errors={errors}
                t={t}
                accent="pink"
                handleInputChange={handleInputChange}
                setMultiSelect={setMultiSelect}
              />

              <div className="flex justify-end space-x-3 pt-4">
                <SharedButton type="button" variant="secondary" onClick={onClose} disabled={loading}>
                  {t.btnCancel}
                </SharedButton>
                <SharedButton
                  type="submit"
                  size="md"
                  disabled={loading}
                  className={`px-6 py-2 text-sm font-medium text-white border border-transparent rounded-xl shadow-md transition-all duration-200 ${
                    loading
                      ? 'btn-gradient-color opacity-50 cursor-not-allowed'
                      : 'btn-gradient-color hover:from-pink-600 hover:to-purple-800 hover:scale-105'
                  }`}
                >
                  {loading ? t.btnSubmitting : t.btnSubmit}
                </SharedButton>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>,
    document.body,
  );
};

export default AcademicRenewalForm;
